import { type Params, useLocation, useNavigate } from "@solidjs/router";
import { stAnalytics } from "@storytell/analytics";
import {
  type ChatMode,
  type ChatPrompt,
  createConversationRequest,
  createInterruptChatRequest,
  type LanguageRegions,
  newStreamPlaybackRequest,
  newStreamRequest,
  PlatformError,
  type PreferredModel,
  type RichText,
  type Scope,
  useCallOptions,
  useChatClient,
  useChatStreamingClient,
  type VendorPreferenceType,
  type VendorStrategyType,
  withBearerToken,
} from "@storytell/cp-client";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import {
  clearAllGranularStores,
  clearBlockContentChunks,
  type StreamingBlockId,
  type StreamingChatId,
  StreamingChatStatus,
  type StreamingMessage,
  type StreamingMessageId,
  StreamingMessageStatus,
  type StreamingPromptAttachment,
  type StreamingService,
} from "@storytell/streaming";
import { createEffect, createMemo, createSignal, on, onCleanup, onMount, untrack } from "solid-js";
import { createStore, type SetStoreFunction } from "solid-js/store";
import { isServer } from "solid-js/web";
import { urls } from "../../urls";
import type { AssetsService } from "../assets";
import { createChatPrefetchCache } from "./chatPrefetchCache";
import { createChatServiceResource, fetchRecentConversation } from "./chatServiceResource";
import { createChatServiceScope } from "./chatServiceScope";
import { createChatTitleWatcher } from "./chatTitleWatcher";
import { watchStream } from "./watchStream";

export type ChatService = ReturnType<typeof createChatService>;

const OLDER_BATCH_SIZE = 20;

// Three missed keepalives: the server sends one at least every ~15s while a turn is live.
const STREAM_STALL_MS = 45_000;
// Returning to the tab resumes a quiet stream early, but not before one keepalive interval has
// passed without a frame, or a healthy stream would be cut.
const RESUME_QUIET_MS = 20_000;
// Each playback replays the whole turn, so retries stop after ~90s of backoff and the chat
// settles from the server's copy instead.
const MAX_RECONNECT_ATTEMPTS = 8;

export type ChatServiceState = {
  chatId: StreamingChatId | undefined;
  error: Error | undefined;
  isStreaming: boolean;
  currentStreamingMessageId: StreamingMessageId | undefined;
  isInterrupting: boolean;
  isReconnecting: boolean;
  // The chat exists but is no longer visible to this user (made private, or
  // removed from a restricted audience). The project itself is still
  // accessible, so this renders inline, not via the project access-denied flow.
  accessLost: boolean;
};
export type SetChatServiceState = SetStoreFunction<ChatServiceState>;

function evictOldestIfOver<K, V>(map: Map<K, V>, maxSize: number): K | undefined {
  while (map.size > maxSize) {
    const first = map.keys().next();
    if (first.done) break;
    map.delete(first.value);
    return first.value;
  }
  return undefined;
}

/**
 * Exponential backoff, jittered so clients cut off together don't retry in lockstep:
 * immediate, then ~1s, 2s, 4s, 8s, 16s, 30s cap.
 */
const getRetryDelay = (attempt: number): number => {
  if (attempt === 0) return 0;
  const base = Math.min(1000 * 2 ** (attempt - 1), 30000);
  return base / 2 + Math.random() * (base / 2);
};

export const createChatService = (deps: {
  urlParams: () => Params;
  authReady: () => boolean;
  authToken: () => string | undefined;
  projectId: () => string | undefined;
  workspaceId: () => string | undefined;
  stream: StreamingService;
  reloadBillingInfo: () => void;
  assets: AssetsService;
  setChatTitle?: (chatId: StreamingChatId, title: string) => void;
  onChatStreamFailed?: (
    error: unknown,
    context: {
      mode?: ChatMode;
      resubmitWithAutoMode: () => void;
      /** The server named the chat before failing, so the turn already has a place in the thread. */
      chatNamed: boolean;
    }
  ) => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = createStore<ChatServiceState>({
    chatId: deps.urlParams().chatId as StreamingChatId | undefined,
    error: undefined,
    isStreaming: false,
    currentStreamingMessageId: undefined,
    isInterrupting: false,
    isReconnecting: false,
    accessLost: false,
  });

  // Visibility per chat, captured from the loaded conversation so the
  // badge/banner work for chats opened outside the recent-chats list.
  const [conversationVisibility, setConversationVisibilityStore] = createStore<Record<string, string>>({});
  // Whether the viewer may change each chat's visibility (creator or project
  // Owner). Unknown chats default to false so the controls stay hidden until
  // the conversation payload arrives.
  const [conversationCanManageVisibility, setConversationCanManageVisibilityStore] = createStore<
    Record<string, boolean>
  >({});

  // Per-chat older-message pagination. olderTotal is the server's total non-deleted
  // message count from the initial load; older messages remain while fewer than that
  // are loaded. loadingOlder guards against concurrent fetches.
  const [olderTotalByChat, setOlderTotalByChat] = createStore<Record<string, number>>({});
  const [loadingOlderByChat, setLoadingOlderByChat] = createStore<Record<string, boolean>>({});

  // Track the current stream's AbortController so we can cancel it when switching chats
  let currentStreamAbortController: AbortController | undefined;

  // Track the last message in the active branch path per chat (set by ChatRenderer).
  // Used by startOrContinueChat to continue on the correct branch.
  const activeLeafByChatId = new Map<StreamingChatId, StreamingMessageId>();
  const activePathMessageIds = new Map<StreamingChatId, StreamingMessageId[]>();

  // Retry state - declared early so abortCurrentStream can reference it
  let retryAttempt = 0;
  let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;
  let reconnectInProgress = false;

  /**
   * Abort any currently running stream. Call this before starting a new stream
   * or when switching chats (see chatServiceResource playback path).
   */
  const abortCurrentStream = () => {
    // Clear retry state first to prevent pending retries
    retryAttempt = 0;
    if (retryTimeoutId) {
      clearTimeout(retryTimeoutId);
      retryTimeoutId = undefined;
    }
    setState("isReconnecting", false);
    reconnectInProgress = false;

    setState({ isStreaming: false, currentStreamingMessageId: undefined });

    if (currentStreamAbortController) {
      currentStreamAbortController.abort();
      currentStreamAbortController = undefined;
    }
  };

  const setCurrentStreamAbortController = (controller: AbortController | undefined) => {
    currentStreamAbortController = controller;
  };

  const clearRetryState = () => {
    retryAttempt = 0;
    if (retryTimeoutId) {
      clearTimeout(retryTimeoutId);
      retryTimeoutId = undefined;
    }
    setState("isReconnecting", false);
  };

  /**
   * Resumes the turn through playback, with exponential backoff between attempts. Playback replays
   * the turn from its start while it is still running; once it has ended the server refuses it and
   * the chat reloads in its final state instead.
   */
  const attemptStreamReconnect = async (messageId: StreamingMessageId, chatId: StreamingChatId) => {
    if (reconnectInProgress) return;
    if (!state.isStreaming) return;
    reconnectInProgress = true;

    const token = deps.authToken();
    if (!token) {
      reconnectInProgress = false;
      clearRetryState();
      return;
    }

    // The stream being replaced may still be open, only silent; left running, it would feed the
    // store alongside the playback.
    currentStreamAbortController?.abort();
    currentStreamAbortController = undefined;

    setState("isReconnecting", true);

    if (retryAttempt >= MAX_RECONNECT_ATTEMPTS) {
      reconnectInProgress = false;
      await settleFromConversation(chatId, token);
      return;
    }

    const delay = getRetryDelay(retryAttempt);
    retryAttempt++;

    if (delay > 0) {
      await new Promise((resolve) => {
        retryTimeoutId = setTimeout(resolve, delay);
      });
      retryTimeoutId = undefined;
    }

    // Check if we were aborted during the delay
    if (!state.isReconnecting) {
      reconnectInProgress = false;
      return;
    }

    const abortController = new AbortController();
    currentStreamAbortController = abortController;
    // Allow new reconnection attempts but keep isReconnecting true
    // until the stream actually succeeds to avoid UI flickering.
    // retryAttempt resets on the first playback chunk, not here, so it backs off between failures
    // and the cap counts consecutive ones.
    reconnectInProgress = false;

    // Keep existing content visible during retries — only clean up when
    // the first playback chunk arrives (confirms connection is live)
    let hasCleanedUp = false;
    const cleanupOnFirstChunk = async function* <T>(source: AsyncIterable<T>): AsyncGenerator<T> {
      for await (const chunk of source) {
        if (!hasCleanedUp) {
          hasCleanedUp = true;
          // A playback that replays and then ends before the stop would otherwise retry with
          // no delay and replay the whole turn in a tight loop.
          retryAttempt = 1;
          // Only clear accumulated block content to prevent duplicate text.
          // Don't nuke the full granular stores or the SolidJS store — active
          // UI components are subscribed to those signals.
          clearBlockContentChunks();
          setState("isReconnecting", false);
        }
        yield chunk;
      }
    };

    try {
      const playbackStream = useChatStreamingClient().streamPlayback(newStreamPlaybackRequest(messageId, chatId, 1.0), {
        bearerToken: token,
        signal: abortController.signal,
      });
      const outcome = await deps.stream.chat.processStreamingChat(
        watch(cleanupOnFirstChunk(playbackStream), abortController),
        abortController.signal
      );

      // Whoever aborted this playback has already taken the turn over.
      if (abortController.signal.aborted) return;

      if (outcome === "interrupted") {
        void attemptStreamReconnect(messageId, chatId);
        return;
      }

      retryAttempt = 0;
      setState("isStreaming", false);
      setState("currentStreamingMessageId", undefined);
      currentStreamAbortController = undefined;
      deps.reloadBillingInfo();
    } catch (error) {
      if (abortController.signal.aborted) return;

      // Playback failed before replaying anything. A request that never got an answer is retried;
      // an answer from the server, most often that the turn is no longer active, settles it.
      const playbackError = new PlatformError(error);
      if (playbackError.isUnavailable() || playbackError.isUnknown()) {
        void attemptStreamReconnect(messageId, chatId);
      } else {
        await settleFromConversation(chatId, token);
      }
    }
  };

  // Loads the chat as the server has it and ends the streaming state. Reconnecting stays set during
  // the fetch so event handlers don't start new reconnection attempts.
  const settleFromConversation = async (chatId: StreamingChatId, token: string) => {
    retryAttempt = 0;
    setState("currentStreamingMessageId", undefined);
    currentStreamAbortController = undefined;

    try {
      const chatClient = useChatClient();
      const conversation = await chatClient.conversation(
        createConversationRequest({ chatId, recentMessagesCount: BigInt(20) }),
        useCallOptions(withBearerToken(token))
      );
      if (conversation.isOk()) {
        // Backoff plus this round trip is long enough for the user to open another
        // chat; replaying then would evict the one they're looking at, and the
        // resource won't re-run because the URL param never changed.
        if (deps.urlParams().chatId === chatId) {
          deps.stream.chat.processChatRecording(conversation.value, new Set([chatId]));
        }
        setState("error", undefined);
      } else {
        setState("error", new Error(conversation.error.message));
      }
    } catch (fallbackError) {
      setState("error", fallbackError as Error);
    }
    setState("isStreaming", false);
    setState("isReconnecting", false);
  };

  // Hands the turn on screen over to playback. Before MESSAGE_START there is no message id to play
  // back, so a stream that stops that early is left to its own error path.
  const resumeTurn = () => {
    const { currentStreamingMessageId: messageId, chatId } = state;
    if (messageId && chatId) void attemptStreamReconnect(messageId, chatId);
  };

  let lastFrameAt = 0;

  // Only the stream behind `controller` may resume the turn on a stall: a stalled stream that has
  // already been replaced must not tear down its successor.
  const watch = <T>(frames: AsyncIterable<T>, controller: AbortController) => {
    lastFrameAt = Date.now();
    return watchStream(
      frames,
      STREAM_STALL_MS,
      () => {
        lastFrameAt = Date.now();
      },
      () => {
        if (currentStreamAbortController === controller) resumeTurn();
      }
    );
  };

  const scope = createChatServiceScope({
    projectId: () => deps.projectId(),
    workspaceId: () => deps.workspaceId(),
    assets: deps.assets,
  });

  // async so a client that throws while building the request rejects instead.
  const prefetchCache = createChatPrefetchCache(async (chatId) => fetchRecentConversation(chatId, deps.authToken()));

  // The chatId whose content is currently resident in the granular stores.
  // processChatRecording clears all granular stores, so at most one chat's
  // content exists at a time; the spinner gate reads this to tell a genuine
  // cold load apart from a revisit hydrated from cache.
  const [lastHydratedChatId, setLastHydratedChatId] = createSignal<StreamingChatId | undefined>(undefined);

  const titleWatcher = createChatTitleWatcher({
    authToken: deps.authToken,
    // The project events stream opens only on routes whose URL carries a project.
    hasEventsStream: () => deps.projectId() !== undefined,
    isGenerating: (chatId) => deps.stream.chat.getStatus(chatId) === StreamingChatStatus.GENERATING,
    fetchTitle: async (chatId, token) => {
      const conversation = await useChatClient().conversation(
        createConversationRequest({ chatId, recentMessagesCount: BigInt(1) }),
        useCallOptions(withBearerToken(token))
      );
      return conversation.isOk() ? conversation.value.title : undefined;
    },
    onTitle: (chatId, title) => {
      deps.setChatTitle?.(chatId, title);
      dispatchGlobalEvent("chat-title-generated", { chatId, title });
    },
  });

  const resource = createChatServiceResource({
    authReady: deps.authReady,
    authToken: deps.authToken,
    urlParams: deps.urlParams,
    projectId: deps.projectId,
    setChatTitle: deps.setChatTitle,
    setConversationVisibility: (chatId, visibility, canManageVisibility) => {
      setConversationVisibilityStore(chatId, visibility);
      setConversationCanManageVisibilityStore(chatId, canManageVisibility);
    },
    setOlderTotal: (chatId, total) => {
      setOlderTotalByChat(chatId, total);
    },
    onMissingTitle: (chatId) => {
      // Chat loaded without a title (e.g. action execution still streaming).
      titleWatcher.watch(chatId);
    },
    state,
    setState,
    stream: deps.stream,
    abortCurrentStream,
    setCurrentStreamAbortController,
    watchStream: watch,
    resumeTurn,
    scope,
    prefetchCache,
    setLastHydratedChatId,
  });

  const startOrContinueChat = (opts: {
    submission: RichText.RichText;
    locale: LanguageRegions.LanguageRegion;
    projectId: string;
    workspaceId: string;
    scope: Scope.Scope;
    suggestionAttribution?: { persona: string; category: string };
    preLoadedSkills?: string[];
    mode?: ChatMode;
    vendorPreference?: VendorPreferenceType;
    vendorStrategy?: VendorStrategyType;
    preferredModel?: PreferredModel;
    webSearchEnabled?: boolean;
    parentMessageId?: string | null;
    onMessageStarted?: (messageId: StreamingMessageId) => void;
    memoryConfig?: ChatPrompt.MemoryConfig;
    retryOfMessageId?: string;
    attachments?: StreamingPromptAttachment[];
  }) => {
    const chat = getChat();
    const chatId = chat?.id;

    const activeLeafMessageId = chatId ? activeLeafByChatId.get(chatId) : undefined;
    const leafInCurrentChat =
      activeLeafMessageId && chat?.messages?.includes(activeLeafMessageId) ? activeLeafMessageId : undefined;

    // opts.parentMessageId semantics:
    //   string    -> explicit fork point, use as-is
    //   null      -> caller explicitly requests no parent (new root turn)
    //   undefined -> not specified: fall back to active leaf or last message
    const parentMessageId =
      opts.parentMessageId !== undefined
        ? opts.parentMessageId === null
          ? undefined
          : opts.parentMessageId
        : (leafInCurrentChat ?? chat?.messages?.at(-1));

    abortCurrentStream();

    const abortController = new AbortController();
    currentStreamAbortController = abortController;

    setState("isStreaming", true);

    const request = newStreamRequest(
      chatId || "",
      opts.submission,
      opts.locale,
      opts.projectId,
      opts.workspaceId,
      opts.scope,
      parentMessageId,
      opts.suggestionAttribution,
      opts.preLoadedSkills,
      opts.mode,
      opts.vendorPreference,
      opts.vendorStrategy,
      opts.memoryConfig,
      opts.webSearchEnabled,
      opts.preferredModel,
      opts.retryOfMessageId,
      opts.attachments
    );
    const token = deps.authToken();

    let capturedOnce = false;
    const onChatIdCaptured = (newChatId: StreamingChatId) => {
      if (capturedOnce) return;
      capturedOnce = true;

      // For existing chats (edit/continue), the user is already on the correct page.
      // Skip navigation but still fire prompt-submitted so the prompt bar clears.
      if (chatId && chatId === newChatId) {
        dispatchGlobalEvent("prompt-submitted-successfuly", undefined);
        setState({ chatId: newChatId, error: undefined });
        return;
      }

      dispatchGlobalEvent("new-chat-created", {
        id: newChatId,
        title: "New Chat",
      });
      dispatchGlobalEvent("prompt-submitted-successfuly", undefined);
      setState({
        chatId: newChatId,
        error: undefined,
      });
      const projectId = deps.projectId();
      if (projectId) {
        const searchParams = new URLSearchParams(location.search);
        const sideParam = searchParams.get("side");

        if (sideParam === "new") {
          // Update side panel URL instead of navigating away from current page
          const currentPath = location.pathname;
          navigate(`${currentPath}?side=${newChatId}`);
        } else {
          navigate(urls.projectPage(projectId, newChatId));
        }
      } else {
        navigate(`/chat/${newChatId}`);
      }

      titleWatcher.watch(newChatId);
    };

    const onMessageStarted = (messageId: StreamingMessageId) => {
      setState("currentStreamingMessageId", messageId);
      // preLoadedSkills and webSearchEnabled are now echoed by MESSAGE_START proto
      // (fields 24/25 on MessageStartData) and mapped in processMessageStart,
      // so no client-side hydration is needed.
      opts.onMessageStarted?.(messageId);
    };

    void deps.stream.chat.startStreamingChat({
      request: request,
      stream: watch(
        useChatStreamingClient().stream(request, { bearerToken: token, signal: abortController.signal }),
        abortController
      ),
      onChatIdCaptured,
      onMessageStarted,
      onMessageFinished: (messageId) => {
        // Only touch the shared view-state when this stream is still the one on
        // screen. A stream backgrounded by a project switch keeps running
        // server-side; its completion must not reset the project the user moved to.
        if (currentStreamAbortController === abortController) {
          setState("isStreaming", false);
          setState("currentStreamingMessageId", undefined);
          currentStreamAbortController = undefined;
        }
        deps.reloadBillingInfo();

        try {
          const message = deps.stream.message.get(messageId);
          if (!message) return;
          const cost = message.aggregateMetrics?.totalCredits ?? 0;
          void stAnalytics.track("credits_consumed", {
            reason: "stream_response",
            amount: cost,
          });
        } catch {
          /* no-op */
        }
      },
      onMessageFailed: (_failedMessageId) => {
        if (currentStreamAbortController === abortController) {
          setState("isStreaming", false);
          setState("currentStreamingMessageId", undefined);
          currentStreamAbortController = undefined;
        }
        deps.reloadBillingInfo();
      },
      onStreamError: (error) => {
        deps.reloadBillingInfo();
        // A stream backgrounded by a project switch keeps running server-side;
        // its failure shouldn't reset the current project's view or surface a
        // toast there. The user sees the failure when they return and reconnect.
        if (currentStreamAbortController !== abortController) {
          return;
        }
        setState("isStreaming", false);
        setState("currentStreamingMessageId", undefined);
        currentStreamAbortController = undefined;
        const resubmitWithAutoMode = () => {
          startOrContinueChat({
            submission: opts.submission,
            locale: opts.locale,
            projectId: opts.projectId,
            workspaceId: opts.workspaceId,
            scope: opts.scope,
            suggestionAttribution: opts.suggestionAttribution,
            preLoadedSkills: opts.preLoadedSkills,
            mode: "auto",
            vendorPreference: opts.vendorPreference,
            vendorStrategy: opts.vendorStrategy,
            preferredModel: opts.preferredModel,
            memoryConfig: opts.memoryConfig,
            webSearchEnabled: opts.webSearchEnabled,
            retryOfMessageId: opts.retryOfMessageId,
            attachments: opts.attachments,
          });
        };
        deps.onChatStreamFailed?.(error, { mode: opts.mode, resubmitWithAutoMode, chatNamed: capturedOnce });
      },
      onStreamInterrupted: () => {
        if (currentStreamAbortController === abortController) resumeTurn();
      },
      abortSignal: abortController.signal,
    });
  };

  const getChatId = () => state.chatId;
  const getChat = createMemo(() => (state.chatId ? deps.stream.state.chats[state.chatId] : undefined));
  const getChatError = () => state.error;
  const isChatStreaming = () => {
    // The flag is optimistic: set at submit, before the store has any record of the turn, so it
    // stands alone until MESSAGE_START lands. From there the store owns the answer and may retract
    // a flag a completion path failed to clear — but only on COMPLETED, since a non-fatal tool
    // error also marks the message FAILED while the turn keeps generating.
    if (state.isStreaming) {
      const messageId = state.currentStreamingMessageId;
      if (!messageId) return true;
      return deps.stream.state.messages[messageId]?.status !== StreamingMessageStatus.COMPLETED;
    }
    if (!state.chatId) return false;
    // activePathMessageIds is maintained by an external createEffect in the renderer layer,
    // which fires asynchronously after the store updates. During the brief window between
    // stream start/stop and that effect running, fall through to the chat-level status check.
    const activeIds = activePathMessageIds.get(state.chatId);
    if (activeIds && activeIds.length > 0) {
      return activeIds.some((id) => deps.stream.state.messages[id]?.status === StreamingMessageStatus.GENERATING);
    }
    return deps.stream.chat.getStatus(state.chatId) === StreamingChatStatus.GENERATING;
  };
  // Only a cold load shows the spinner: a revisit or warmed chat hydrates from
  // cache, so once its content is the resident one the resource may still be
  // mid-revalidation but there is nothing to wait for.
  const isChatLoading = () => resource.loading && lastHydratedChatId() !== state.chatId;
  const isInterrupting = () => state.isInterrupting;
  const isReconnecting = () => state.isReconnecting;

  // Sync draft scope from the last *turn* only when that turn's message id changes — not on every
  // streaming/store update to the same message. Otherwise loadScopeFromMessage re-runs during the
  // assistant response and wipes an explicitly cleared scope before the user submits the next prompt.
  createEffect(
    on(
      () => {
        const chat = getChat();
        return chat?.messages?.length ? chat.messages[chat.messages.length - 1] : undefined;
      },
      (lastMessageId, _prevLastMessageId) => {
        if (!lastMessageId) {
          return;
        }
        const lastMessage = deps.stream.state.messages[lastMessageId];
        const messageScope = lastMessage?.prompt.scope;
        if (messageScope) {
          untrack(() => {
            scope.loadScopeFromMessage(messageScope);
          });
        }
        // When the last message has no scope (e.g. after Back from side panel, or API didn't return it),
        // do not clear scope so the "You Are Chatting With" panel stays in sync with the prompt bar.
      }
    )
  );

  const shouldShowContent = () => {
    if (getChatId()) return true;
    if (isChatStreaming()) return true;
    if (isChatLoading()) return true;

    return false;
  };

  const getChatMessages = () => {
    const chatId = getChatId();
    return chatId
      ? deps.stream.chat
          .get(chatId)
          ?.messages.map((m) => deps.stream.message.get(m))
          .filter((m): m is StreamingMessage => m !== undefined)
      : undefined;
  };
  const getBlock = (blockId: StreamingBlockId) => deps.stream.block.get(blockId);

  const interruptStream = async (_userInput = "") => {
    const messageId = state.currentStreamingMessageId;
    if (!messageId) {
      return;
    }

    if (state.isInterrupting) {
      return;
    }

    const token = deps.authToken();
    if (!token) {
      return;
    }

    try {
      setState("isInterrupting", true);

      const chatClient = useChatClient();
      const request = createInterruptChatRequest({
        messageId: messageId,
      });

      const result = await chatClient.interruptChat(request, useCallOptions(withBearerToken(token)));

      if (!result.isOk()) {
        throw new Error(`Failed to interrupt chat: ${result.error.message}`);
      }

      if (result.value.success) {
        setState("isStreaming", false);
        setState("currentStreamingMessageId", undefined);

        if (state.chatId) {
          deps.reloadBillingInfo();

          try {
            const message = deps.stream.message.get(messageId);
            if (message) {
              const cost = message.aggregateMetrics?.totalCredits ?? 0;
              void stAnalytics.track("credits_consumed", {
                reason: "stream_response",
                amount: cost,
              });
            }
          } catch (_err) {
            // Failed to track interrupted message metrics
          }
        }
      } else {
        throw new Error("Failed to interrupt chat: Request was not successful");
      }
    } finally {
      setState("isInterrupting", false);
    }
  };

  const loadedMessageCount = (chatId: StreamingChatId) => deps.stream.state.chats[chatId]?.messages.length ?? 0;
  const isLoadingOlder = () => {
    const chatId = state.chatId;
    return chatId ? (loadingOlderByChat[chatId] ?? false) : false;
  };
  const hasOlderMessages = () => {
    const chatId = state.chatId;
    if (!chatId) return false;
    const total = olderTotalByChat[chatId];
    if (total === undefined) return false;
    const loaded = loadedMessageCount(chatId);
    return loaded > 0 && loaded < total;
  };

  const loadOlderMessages = async () => {
    const chatId = state.chatId;
    if (!chatId) return;
    if (isLoadingOlder()) return;
    if (!hasOlderMessages()) return;

    // The chat list is kept oldest-first, so its first entry is the cursor for the next batch.
    const messages = deps.stream.state.chats[chatId]?.messages ?? [];
    const oldestId = messages[0];
    if (!oldestId) return;

    const token = deps.authToken();
    if (!token) return;

    setLoadingOlderByChat(chatId, true);
    try {
      const chatClient = useChatClient();
      const conversation = await chatClient.conversation(
        createConversationRequest({
          chatId,
          recentMessagesCount: BigInt(OLDER_BATCH_SIZE),
          beforeMessageId: oldestId,
        }),
        useCallOptions(withBearerToken(token))
      );

      if (conversation.isOk()) {
        const added = deps.stream.chat.mergeOlderRecordings(conversation.value);
        // A load that adds nothing means the start of history (or a branch edge);
        // pin the total to what's loaded so the control stops offering a no-op fetch.
        if (added === 0) {
          setOlderTotalByChat(chatId, loadedMessageCount(chatId));
        }
      } else {
        setState("error", new Error(conversation.error.message));
      }
    } catch (error) {
      setState("error", error as Error);
    } finally {
      setLoadingOlderByChat(chatId, false);
    }
  };

  const resetChat = () => {
    clearAllGranularStores();
    // Granular stores only hold deltas; the messages, operations and blocks are
    // in the streaming store and stay resident without this.
    deps.stream.chat.evictExcept(new Set<StreamingChatId>());
    setLastHydratedChatId(undefined);
    scope.clearAll();
    activeLeafByChatId.clear();
    activePathMessageIds.clear();
    setState({
      chatId: undefined,
      error: undefined,
      isStreaming: false,
      currentStreamingMessageId: undefined,
      isInterrupting: false,
      isReconnecting: false,
      accessLost: false,
    });
  };

  // Never calls processChatRecording — that clears the active chat's granular
  // content; warming only fills the cache the resource hydrates from. Callers
  // pass a handful of ids (the idle top few, or one on hover/focus), so every
  // request starts at once.
  const prefetchChats = (chatIds: StreamingChatId[]) => {
    if (isServer || !deps.authToken()) return;
    for (const chatId of chatIds) {
      if (chatId === deps.urlParams().chatId) continue;
      if (isChatStreaming() && state.chatId === chatId) continue;
      if (prefetchCache.has(chatId)) continue;
      prefetchCache.load(chatId).catch((error: unknown) => {
        console.error("chatService: prefetch failed:", { chatId, error });
      });
    }
  };

  const forceEndStreaming = () => {
    setState("isStreaming", false);
    setState("currentStreamingMessageId", undefined);
    setState("isInterrupting", false);
    setState("isReconnecting", false);
  };

  // Reset chat when visiting homepage (new chat)
  createEffect(
    on(
      () => location.pathname,
      (pathname) => {
        if (isServer) return;
        if (pathname === "/" || /^\/project\/[^/]+\/?$/.test(pathname)) {
          resetChat();
        }
      }
    )
  );

  onMount(() => {
    const unsubscribeUserContextChanged = subscribeToGlobalEvent("user-context-changed", (event) => {
      // Don't reset chat if the context switch should preserve the current chat
      // (e.g., when navigating to a project-scoped chat URL)
      if (event.detail?.preserveChat) {
        return;
      }

      // Detach the local subscription on project/workspace switch. Generation
      // continues server-side and reconnects via playback on return, so holding
      // the connection open here would only waste it.
      abortCurrentStream();
      resetChat();
      // resetChat spares generating chats; these belong to the workspace being
      // left, so nothing here will render them again.
      deps.stream.chat.evictExcept(new Set<StreamingChatId>(), { includeGenerating: true });
      // Drop cached payloads and in-flight requests from the project being left,
      // so a late response can't repopulate the cleared cache with old chats.
      prefetchCache.clear();
    });

    // The visibility modal saves then dispatches; recording it here keeps the
    // store current even when the conversation fetcher skips (streaming chat).
    const unsubscribeVisibilityUpdated = subscribeToGlobalEvent("chat-visibility-updated", (event) => {
      setConversationVisibilityStore(event.detail.chatId, event.detail.visibility);
    });

    // The current user created this chat, so they can manage its visibility.
    // The conversation fetcher skips while the chat streams, so without this the
    // controls stay hidden until a refresh re-runs that fetch.
    const unsubscribeNewChatCreated = subscribeToGlobalEvent("new-chat-created", (event) => {
      setConversationCanManageVisibilityStore(event.detail.id, true);
    });

    // A deleted chat can't self-heal via background revalidation (the refetch
    // 404s), so evict it here the moment the delete is dispatched.
    const unsubscribeChatDeleted = subscribeToGlobalEvent("chat-deleted", (event) => {
      prefetchCache.delete(event.detail.chatId);
    });

    const unsubscribeChatMoved = subscribeToGlobalEvent("chat-moved", (event) => {
      for (const chatId of event.detail.chatIds) {
        prefetchCache.delete(chatId);
      }
    });

    // When browser goes offline, move the active stream onto the reconnection path.
    // Without this, existing TCP connections may survive the browser's offline toggle
    // and the stream continues receiving data despite the user being "offline".
    const unsubscribeConnectivityChanged = subscribeToGlobalEvent("connectivity-changed", (event) => {
      if (!event.detail.isOnline && !state.isReconnecting) resumeTurn();
    });

    // iOS Safari suspends a background tab's fetch without failing it, so a stream that went quiet
    // while the tab was hidden is resumed on return instead of after the full stall window.
    const resumeIfQuiet = () => {
      if (document.visibilityState !== "visible" || !state.isStreaming || !currentStreamAbortController) return;
      if (Date.now() - lastFrameAt >= RESUME_QUIET_MS) resumeTurn();
    };
    document.addEventListener("visibilitychange", resumeIfQuiet);
    window.addEventListener("pageshow", resumeIfQuiet);

    // When connectivity is restored, cancel any pending backoff and immediately retry playback.
    // Don't call clearRetryState() — it sets isReconnecting=false which opens a TOCTOU window.
    const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
      if (state.isStreaming && state.currentStreamingMessageId && state.chatId && state.isReconnecting) {
        const messageId = state.currentStreamingMessageId;
        const chatId = state.chatId;
        retryAttempt = 0;
        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
          retryTimeoutId = undefined;
        }
        reconnectInProgress = false;
        void attemptStreamReconnect(messageId, chatId);
      }
    });

    onCleanup(() => {
      unsubscribeUserContextChanged();
      unsubscribeVisibilityUpdated();
      unsubscribeNewChatCreated();
      unsubscribeChatDeleted();
      unsubscribeChatMoved();
      unsubscribeConnectivityChanged();
      unsubscribeConnectivityRestored();
      document.removeEventListener("visibilitychange", resumeIfQuiet);
      window.removeEventListener("pageshow", resumeIfQuiet);
    });
  });

  return {
    scope,
    getChatId,
    shouldShowContent,
    getChat,
    getConversationVisibility: (chatId: StreamingChatId) => conversationVisibility[chatId],
    canManageConversationVisibility: (chatId: StreamingChatId) => conversationCanManageVisibility[chatId] ?? false,
    getChatError,
    isAccessLost: () => state.accessLost,
    isChatStreaming,
    isChatLoading,
    prefetchChats,
    isInterrupting,
    isReconnecting,
    startOrContinueChat,
    interruptStream,
    hasOlderMessages,
    isLoadingOlder,
    loadOlderMessages,
    resetChat,
    getActiveLeafMessageId: (chatId: StreamingChatId) => activeLeafByChatId.get(chatId),
    setActiveLeafMessageId: (chatId: StreamingChatId, id: StreamingMessageId | undefined) => {
      if (id) {
        activeLeafByChatId.set(chatId, id);
        const evicted = evictOldestIfOver(activeLeafByChatId, 20);
        if (evicted) activePathMessageIds.delete(evicted);
      } else {
        activeLeafByChatId.delete(chatId);
        activePathMessageIds.delete(chatId);
      }
    },
    setActivePathMessageIds: (chatId: StreamingChatId, ids: StreamingMessageId[]) => {
      activePathMessageIds.set(chatId, ids);
      const evicted = evictOldestIfOver(activePathMessageIds, 20);
      if (evicted) activeLeafByChatId.delete(evicted);
    },
    forceEndStreaming,
    getChatMessages,
    getBlock,
  };
};
